/**
 * Clients and their requisitions.
 *
 * Jobs share `entitlement: 'clients'` — a requisition belongs to a client, and an agency that
 * has bought the clients module has bought the ability to open one against them.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const CLIENT_ROUTES: readonly RouteManifestEntry[] = [
  // The first genuinely gated routes. A tenant without `clients` sees no nav item, and typing the
  // URL renders the entitlement notice — while the API refuses the call independently, because a
  // UI check is a courtesy and the server is the enforcement.
  {
    id: 'clients',
    path: '/clients',
    entitlement: 'clients',
    authenticated: true,
    nav: { labelKey: 'shell.nav.clients', section: 'recruiting', order: 1 },
    load: () => import('../screens/clients.js'),
  },
  {
    id: 'client-new',
    path: '/clients/new',
    entitlement: 'clients',
    // Adding a client is an administrator's decision: a client carries commercial terms, fee
    // bases and ownership windows, and a list anyone can append to is a list nobody maintains.
    // `administer`, matching `clients.client.create`. Signing a client is an administrative act;
    // `write client` now means the status change, which a manager holds. Leaving this as `write`
    // would offer a manager an Add-client screen whose submit button 403s.
    requires: { action: 'administer', resource: 'client' },
    authenticated: true,
    load: () => import('../screens/client-new.js'),
  },
  {
    id: 'client-detail',
    path: '/clients/$clientId',
    entitlement: 'clients',
    authenticated: true,
    load: () => import('../screens/client-detail.js'),
  },
  // Jobs share the `clients` entitlement, because a requisition is the client's order — the same
  // reason the routes live in that module. What differs is the permission: `write job` reaches
  // down to `recruiter`, where `write client` stops at `admin`.
  {
    id: 'jobs',
    path: '/jobs',
    entitlement: 'clients',
    authenticated: true,
    nav: { labelKey: 'shell.nav.jobs', section: 'recruiting', order: 2 },
    /*
     * §6.2's filters, in the URL — roadmap F1, and the reason it was blocked until now.
     *
     * No job route declared a `validateSearch`, so no job screen had URL-addressable filter state:
     * a filtered list could not be linked, bookmarked or reloaded, and "saved filter combinations"
     * had nothing to save. Every value is passed through as a plain string and validated by the
     * API, exactly as the interview tracker does it — a screen that refused a bad value in the URL
     * bar would 404 where the server would 422, and the server's message is the one that names the
     * field.
     *
     * Unknown keys are dropped rather than carried, which is what makes the set here the filter
     * vocabulary rather than a suggestion.
     */
    validateSearch: (search) =>
      Object.fromEntries(
        (
          [
            'search',
            'status',
            'clientId',
            'employmentType',
            'workModel',
            'priority',
            'ownerMembershipId',
            'owningGroupId',
            'openedBeforeDays',
            'attention',
            /* §6.1's inaction bucket. Its own key, so it composes with `attention` in the URL. */
            'inaction',
            'sort',
            'scope',
          ] as const
        )
          .filter((key) => typeof search[key] === 'string' && search[key] !== '')
          .map((key) => [key, search[key] as string]),
      ),
    load: () => import('../screens/jobs.js'),
  },
  {
    id: 'job-new',
    path: '/jobs/new',
    entitlement: 'clients',
    /*
     * `copyFrom` puts §3.3's fourth creation mode in the URL.
     *
     * The chooser is one entry point and a record's own "duplicate" action is the other, and they
     * are different intentions — "I need a requisition and one like it exists" against "another
     * seat on this one". Carrying the source in the URL is what lets the second land on the same
     * screen as the first instead of growing a parallel one.
     */
    /*
     * `forClient` is the second, and it carries *context* rather than a source — 28 Aug 2026.
     *
     * Reported: *"while adding a job under a client, the context should be there and selecting a
     * client should not be asked."* Opening a requisition from a client's own screen and then being
     * asked which client it is for is the product forgetting something the person just told it, and
     * the picker is not merely redundant — it is a way to file the job against the wrong client
     * while standing on the right one's page.
     *
     * In the URL rather than in a store, for the same reason `copyFrom` is: the screen is reachable
     * by a link, a refresh must not lose it, and a state held in memory would be gone on both.
     */
    validateSearch: (search) => ({
      ...(typeof search.copyFrom === 'string' && search.copyFrom !== ''
        ? { copyFrom: search.copyFrom }
        : {}),
      ...(typeof search.forClient === 'string' && search.forClient !== ''
        ? { forClient: search.forClient }
        : {}),
    }),
    // A requisition is daily operational work rather than a commercial decision, so a recruiter
    // opens one. That is the deliberate difference from /clients/new above.
    requires: { action: 'write', resource: 'job' },
    authenticated: true,
    load: () => import('../screens/job-new.js'),
  },
  /*
   * The job record — §7.2's eight sections, as eight sibling routes.
   *
   * Flat rather than nested, because `router.tsx` builds one level from this manifest so that
   * FE-02's gate is reading the real routing table. Each tab is therefore its own entry, and that
   * turns out to be the reason to want it: **three of the eight belong to entitlements a tenant may
   * not hold**, and declaring that here means the gate checks it at build time rather than a
   * component checking it at render time.
   *
   * Overview keeps `/jobs/$jobId` rather than moving to `/jobs/$jobId/overview`. Every link in the
   * product already points there, the screen already holds the requirement and §5.4's status gate,
   * and a redirect would have bought nothing but a hop. `record-layout.tsx` has the rest of the
   * argument; `job-pipeline` lives in the pipeline manifest, where its entitlement does.
   */
  {
    id: 'job-detail',
    path: '/jobs/$jobId',
    entitlement: 'clients',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  {
    id: 'job-workbench',
    path: '/jobs/$jobId/workbench',
    entitlement: 'clients',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
];
